// src/calculator/PriceLockDialog.jsx
//
// Shown when a service price is being changed and there are open bookings.
// The owner chooses whether to honour the old price for existing bookings
// or apply the new price everywhere.
//
// Props:
//   open            boolean
//   onClose         () => void           — cancel / do nothing
//   onHonour        (effectiveDate) => void  — existing_bookings = 'hold'
//   onApplyAll      (effectiveDate) => void  — existing_bookings = 'move'
//   effectiveDate   string (YYYY-MM-DD) | ''  — date the new prices start
//   bookingsAffected  number
//   changedTiers    [{ tier_name, old_price, new_price }]  — for tier path
//   oldPrice        number | null        — for flat price path
//   newPrice        number | null        — for flat price path

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Table, TableBody,
  TableCell, TableHead, TableRow, Alert, TextField,
} from '@mui/material';
import LockIcon        from '@mui/icons-material/Lock';
import LockOpenIcon    from '@mui/icons-material/LockOpen';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const fmt = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n || 0));

export default function PriceLockDialog({
  open,
  onClose,
  onHonour,
  onApplyAll,
  bookingsAffected = 0,
  changedTiers = [],
  oldPrice = null,
  newPrice = null,
  effectiveDate = '',
}) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(effectiveDate || todayStr);
  useEffect(() => { if (open) setDate(effectiveDate || todayStr); }, [open, effectiveDate, todayStr]);
  const isFuture = date > todayStr;
  // Determine display mode: tier-based or flat price
  const hasTiers = changedTiers && changedTiers.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#BF360C' }} />
          Price change — existing bookings
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{bookingsAffected} existing booking{bookingsAffected !== 1 ? 's' : ''}</strong>{' '}
          {bookingsAffected === 1 ? 'was' : 'were'} made at the current{' '}
          {hasTiers ? 'tier prices' : 'price'}.
          What would you like to do?
        </Alert>

        {/* Flat price change summary */}
        {!hasTiers && oldPrice !== null && newPrice !== null && (
          <Box sx={{
            p: 2, mb: 2, borderRadius: 1,
            bgcolor: '#f5f5f5', border: '1px solid #e0e0e0',
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Price change
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                {fmt(oldPrice)}
              </Typography>
              <Typography variant="body2" color="text.secondary">→</Typography>
              <Typography variant="h6" color="primary.main">{fmt(newPrice)}</Typography>
            </Box>
          </Box>
        )}

        {/* Tier price change table */}
        {hasTiers && (
          <Box sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tier</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Old price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>New price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changedTiers.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell>{t.tier_name}</TableCell>
                    <TableCell align="right" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                      {fmt(t.old_price)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {fmt(t.new_price)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <TextField
          type="date" size="small" label="New prices start on"
          value={date} onChange={(e) => setDate(e.target.value || todayStr)}
          InputLabelProps={{ shrink: true }} inputProps={{ min: todayStr }}
          helperText={isFuture
            ? 'Appointments before this date stay at the old price.'
            : 'Today — the new prices start straight away.'}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Option 1 — honour old price */}
          <Box sx={{
            p: 2, borderRadius: 1, border: '2px solid',
            borderColor: 'success.light', bgcolor: '#f1f8e9',
            cursor: 'pointer',
            '&:hover': { borderColor: 'success.main', bgcolor: '#e8f5e9' },
          }}
            onClick={() => onHonour(date)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LockIcon fontSize="small" sx={{ color: '#2e7d32' }} />
              <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}>
                Keep existing bookings at their booked price
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Existing bookings are charged what was quoted when they were made.
              New bookings use the new price.
            </Typography>
          </Box>

          {/* Option 2 — apply everywhere */}
          <Box sx={{
            p: 2, borderRadius: 1, border: '2px solid',
            borderColor: 'warning.light', bgcolor: '#fffde7',
            cursor: 'pointer',
            '&:hover': { borderColor: 'warning.main', bgcolor: '#fff9c4' },
          }}
            onClick={() => onApplyAll(date)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LockOpenIcon fontSize="small" sx={{ color: '#BF360C' }} />
              <Typography variant="subtitle2" sx={{ color: '#BF360C' }}>
                Move existing bookings to the new price
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Open bookings from the start date onwards, for the stylists on these tiers,
              are re-priced to the new price.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel — don't save yet</Button>
      </DialogActions>
    </Dialog>
  );
}